import React from 'react';
import { FaEdit } from '@react-icons/all-files/fa/FaEdit';
import { FiRefreshCw } from '@react-icons/all-files/fi/FiRefreshCw';
import { FiPlus } from '@react-icons/all-files/fi/FiPlus';
import { FiTrash2 } from '@react-icons/all-files/fi/FiTrash2';
import { FaChevronDown } from '@react-icons/all-files/fa/FaChevronDown';
import { FaChevronUp } from '@react-icons/all-files/fa/FaChevronUp';
import { Select, Button } from 'antd';
import { ALL_SKILLS } from './constants';
import './index.scss';

const SKILL_LEVELS = [
  { value: undefined, label: 'Not applicable' },
  { value: 1, label: 'Novice' },
  { value: 2, label: 'Beginner' },
  { value: 3, label: 'Skillful' },
  { value: 4, label: 'Experienced' },
  { value: 5, label: 'Expert' },
];

function getRandomSkills(exclude = [], count = 7) {
  const available = ALL_SKILLS.filter(s => !exclude.includes(s));
  const shuffled = available.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

const initialSuggested = [
  'Problem Solving',
  'Research Skills',
  'Interpersonal Skills',
  'Sales Strategy',
  'Market Analysis',
  'Negotiation Skills',
  'Process Improvement',
];

const SkillsStep = (props) => {
  const { params, onChange } = props;
  const skills = params.skills || [];
  const [refreshing, setRefreshing] = React.useState(false);
  const [suggestedSkills, setSuggestedSkills] = React.useState(initialSuggested);
  const [openSkills, setOpenSkills] = React.useState(new Set());
  const inputRefs = React.useRef([]);

  const onContinueClick = () => {
    props.onContinueClick && props.onContinueClick();
  };

  const onBackClick = () => {
    props.onBackClick && props.onBackClick();
  };

  const addSkill = (name = '') => {
    const newSkills = [...skills, { name, level: undefined }];
    onChange('skills', newSkills);
    // Auto-open the newly added skill
    const newIndex = newSkills.length - 1;
    setOpenSkills(prev => new Set([...prev, newIndex]));
    setTimeout(() => {
      if (inputRefs.current[newIndex]) {
        inputRefs.current[newIndex].focus();
      }
    }, 0);
  };

  const removeSkill = (idx) => {
    const newSkills = skills.filter((_, i) => i !== idx);
    onChange('skills', newSkills);
    // Remove from openSkills if it was open
    setOpenSkills(prev => {
      const newOpen = new Set(prev);
      newOpen.delete(idx);
      // Adjust indices for items after the removed one
      const adjustedOpen = new Set();
      newOpen.forEach(openIdx => {
        if (openIdx < idx) {
          adjustedOpen.add(openIdx);
        } else {
          adjustedOpen.add(openIdx - 1);
        }
      });
      return adjustedOpen;
    });
  };

  const updateSkill = (idx, field, value) => {
    const newSkills = skills.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    onChange('skills', newSkills);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      setRefreshing(false);
      setSuggestedSkills(getRandomSkills(skills.map(s => s.name), 7));
    }, 500);
  };

  const handleSuggestedClick = (skill) => {
    setSuggestedSkills(suggestedSkills.filter(s => s !== skill));
    addSkill(skill);
  };

  const toggleSkillCollapse = (idx) => {
    setOpenSkills(prev => {
      const newOpen = new Set(prev);
      if (newOpen.has(idx)) {
        newOpen.delete(idx);
      } else {
        newOpen.add(idx);
      }
      return newOpen;
    });
  };

  return (
    <div className="step skills-step">
      <div className="step-inner-content">
        <div className="step-header">
          <h2>Skills</h2>
          <p>Highlight your most important and applicable professional skills.</p>
        </div>
        <div className="skills-bar">
          <span className="suggested-chip"
            onClick={handleRefresh}
          >✧ Suggested | Refresh <FiRefreshCw size={16} className={refreshing ? 'spin' : ''} /></span>
          {suggestedSkills.map((skill, i) => (
            <span className="skill-chip" key={skill} onClick={() => handleSuggestedClick(skill)}>{skill}</span>
          ))}
        </div>
        <div className="skills-list">
          {skills.map((skill, idx) => (
            <div className="skill-item" key={idx}>
              <div className="left">
                <div className="skill-header">
                  <div className="skill-main" onClick={() => toggleSkillCollapse(idx)}>
                    <div className="skill-name" style={{ fontWeight: 400 }}>
                      {skill.name || 'Untitled'}
                      {skill.level && <span className="skill-level-indicator"> • Level {skill.level}</span>}
                    </div>
                    <div className="chevron-icon">
                      {!openSkills.has(idx) ? <FaChevronDown size={16} /> : <FaChevronUp size={16} />}
                    </div>
                  </div>
                </div>
                {openSkills.has(idx) && (
                  <div className="skill-details">
                    <div className="field">
                      <label>Skill</label>
                      <input
                        type="text"
                        placeholder="Skill name"
                        value={skill.name}
                        onChange={e => updateSkill(idx, 'name', e.target.value)}
                        list="all-skills-list"
                        ref={el => (inputRefs.current[idx] = el)}
                      />
                      <datalist id="all-skills-list">
                        {ALL_SKILLS.map(s => <option key={s} value={s} />)}
                      </datalist>
                    </div>
                    <div className="field">
                      <label>Level</label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select skill level"
                        value={skill.level}
                        onChange={val => updateSkill(idx, 'level', val)}
                        options={SKILL_LEVELS}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="right">
                <div className="delete-btn" onClick={(e) => { e.stopPropagation(); removeSkill(idx); }} title="Remove skill">
                  <FiTrash2 size={19} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="add-object-row">
          <button className="add-object-btn" onClick={() => addSkill()}>
            <FiPlus size={22} /> Add one more skill
          </button>
        </div>
      </div>
      <div className="step-footer">
        <button className="back-btn" onClick={onBackClick}>&larr; Back</button>
        <Button loading={props.formLoading} className="next-btn" onClick={onContinueClick}>Next <span className="arrow">&rarr;</span></Button>
      </div>
    </div>
  );
};

export default SkillsStep;
